const TOKEN_STORAGE_KEY = "token";
const AUTH_SCOPE = "openid profile email offline_access";

let accessTokenGetter = null;

const canUseLocalStorage = () => typeof window !== "undefined" && window.localStorage;

export const getAuthAudience = () => process.env.REACT_APP_AUDIENCE_URL;
export const getAuthScope = () => AUTH_SCOPE;

export const setAccessTokenGetter = (getter) => {
  accessTokenGetter = typeof getter === "function" ? getter : null;
};

export const clearAccessTokenGetter = () => {
  accessTokenGetter = null;
};

export const getStoredAccessToken = () => {
  if (!canUseLocalStorage()) return null;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
};

export const setStoredAccessToken = (token) => {
  if (!canUseLocalStorage()) return;
  if (token) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  }
};

export const clearStoredAccessToken = () => {
  if (!canUseLocalStorage()) return;
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
};

export const isTokenExpiredPayload = (payload) => {
  return payload?.name === "TokenExpiredError" || payload?.type === 4;
};

export const isTokenExpiredResponse = (response) => {
  return isTokenExpiredPayload(response?.data) || response?.status === 401;
};

export const getFreshAccessToken = async (options = {}) => {
  const { forceRefresh = false, optional = false } = options;

  if (accessTokenGetter) {
    try {
      const token = await accessTokenGetter({ forceRefresh });
      if (token) {
        setStoredAccessToken(token);
        return token;
      }
    } catch (error) {
      const storedToken = getStoredAccessToken();
      if (!storedToken) {
        if (optional) {
          return null;
        }
        throw error;
      }
    }
  }

  return getStoredAccessToken();
};

export const refreshAccessToken = async () => {
  return getFreshAccessToken({ forceRefresh: true });
};

export const forceAuthLogout = () => {
  clearStoredAccessToken();
  if (typeof window !== "undefined" && window.location.pathname !== "/logout") {
    window.location.pathname = "/logout";
  }
};

const mergeHeadersWithToken = (headers = {}, token) => {
  const nextHeaders = {
    ...headers,
  };

  if (token) {
    nextHeaders.Authorization = `Bearer ${token}`;
  }

  return nextHeaders;
};

const shouldRetryFetchResponse = async (response) => {
  if (response?.status === 401) return true;

  try {
    const payload = await response.clone().json();
    return isTokenExpiredPayload(payload);
  } catch (_) {
    return false;
  }
};

export const authFetch = async (url, options = {}) => {
  const optionalAuth = options.optionalAuth === true;
  const token = await getFreshAccessToken({ optional: optionalAuth });
  const requestOptions = {
    ...options,
    headers: mergeHeadersWithToken(options.headers, token),
  };
  delete requestOptions.optionalAuth;

  const response = await fetch(url, requestOptions);
  const shouldRetry = await shouldRetryFetchResponse(response);

  if (!shouldRetry) {
    return response;
  }

  try {
    const refreshedToken = await refreshAccessToken();
    if (!refreshedToken || refreshedToken === token) {
      forceAuthLogout();
      return response;
    }

    return fetch(url, {
      ...options,
      headers: mergeHeadersWithToken(options.headers, refreshedToken),
    });
  } catch (_) {
    forceAuthLogout();
    return response;
  }
};
